(function () {
	"use strict";

	const SELECTORS = {
		searchInput: "[data-blog-search]",
		grid: ".blog-posts-container",
		item: ".blog-post-item",
		tagFilter: "[data-blog-tags-filter]",
		tagButton: "[data-blog-tag]",
		loadMoreButton: "[data-blog-load-more]",
	};

	const DATA_ATTRS = {
		searchTitle: "data-search-title",
		searchTags: "data-search-tags",
		searchTagHandles: "data-tag-handles",
		searchExcerpt: "data-search-excerpt",
	};

	const TAG_ALL = "all";

	/**
	 * Class component: 1 instance = 1 section blog (search + tag + grid).
	 */
	class BlogSearchFilter {
		/**
		 * @param {Element} root - The root element of the section (e.g. <blog-posts-list>)
		 */
		constructor(root) {
			// --- Refs ---
			this.root = root;
			this.searchInput = root.querySelector(SELECTORS.searchInput);
			this.grid = root.querySelector(SELECTORS.grid);
			this.tagFilterEl = root.querySelector(SELECTORS.tagFilter);
			this.tagButtons = this.tagFilterEl
				? this.tagFilterEl.querySelectorAll(SELECTORS.tagButton)
				: [];
			this.loadMoreButton = root.querySelector(SELECTORS.loadMoreButton);

			// --- State: the selected tag ---
			this.activeTag = TAG_ALL;
			const initialTag = this.tagFilterEl?.getAttribute("data-initial-tag");
			if (initialTag) {
				this.activeTag = (initialTag || "").trim().toLowerCase() || TAG_ALL;
			}

			// --- Bind to ensure "this" is correct in events ---
			this._boundApplyFilters = this.applyFilters.bind(this);
			this._boundOnTagClick = this._onTagClick.bind(this);
			this._boundOnLoadMoreClick = this._onLoadMoreClick.bind(this);

			// --- Setup + run filter initially ---
			this._setupSearchInput();
			this._setupTagButtons();
			this._setupLoadMoreButton();
			this.applyFilters();
		}

		/** Get the search query (trim, lowercase). */
		getSearchQuery() {
			const value = this.searchInput?.value ?? "";
			return String(value).trim().toLowerCase();
		}

		/** Change the selected tag and update the tag button UI. */
		setActiveTag(tag) {
			this.activeTag = (tag || TAG_ALL).trim().toLowerCase() || TAG_ALL;
			this._updateTagButtonsUI();
		}

		/** Does the item match the search query (title / tags / excerpt)? */
		itemMatchesSearch(item, q) {
			if (!q) return true;
			const title = (item.getAttribute(DATA_ATTRS.searchTitle) || "").toLowerCase();
			const tags = (item.getAttribute(DATA_ATTRS.searchTags) || "").toLowerCase();
			const excerpt = (item.getAttribute(DATA_ATTRS.searchExcerpt) || "").toLowerCase();
			return title.includes(q) || tags.includes(q) || excerpt.includes(q);
		}

		/** Does the item belong to the selected tag? */
		itemMatchesTag(item) {
			if (this.activeTag === TAG_ALL) return true;
			const handles = (item.getAttribute(DATA_ATTRS.searchTagHandles) || "").toLowerCase();
			if (!handles) return false;
			const target = this.activeTag.toLowerCase();
			// Wrap with '|' on both sides to avoid substring matching
			const wrapped = "|" + handles + "|";
			return wrapped.includes("|" + target + "|");
		}

		/** Apply the filter: show/hide each item based on search + tag. Return the number of items displayed. */
		applyFilters() {
			if (!this.grid) return 0;
			const query = this.getSearchQuery();
			const items = this.grid.querySelectorAll(SELECTORS.item);
			let visibleCount = 0;

			items.forEach((item) => {
				const matchSearch = this.itemMatchesSearch(item, query);
				const matchTag = this.itemMatchesTag(item);
				const shouldShow = matchSearch && matchTag;
				item.style.display = shouldShow ? "" : "none";
				if (shouldShow) visibleCount += 1;
			});

			return visibleCount;
		}

		/** Update the tag button UI (is-active, aria-pressed). */
		_updateTagButtonsUI() {
			this.tagButtons.forEach((btn) => {
				if (!btn) return;
				const btnTag = (btn.getAttribute("data-blog-tag") || "").trim().toLowerCase();
				const isActive = btnTag === this.activeTag;
				btn.classList.toggle("is-active", isActive);
				btn.setAttribute("aria-pressed", isActive ? "true" : "false");
			});
		}

		/** Handler click on tag button. */
		_onTagClick(e) {
			const btn = e.currentTarget;
			const tag = btn?.getAttribute("data-blog-tag") || TAG_ALL;
			this.setActiveTag(tag);
			const visible = this.applyFilters();

			// If after selecting a tag and no articles are displayed, try to automatically load one more page
			// to avoid the "empty" state that is confusing for the user.
			if (!visible) {
				this._ensureResultsForCurrentFilters();
			}
		}

		/** Attach event to search input. */
		_setupSearchInput() {
			if (!this.searchInput) return;
			this.searchInput.addEventListener("input", this._boundApplyFilters);
			this.searchInput.addEventListener("keyup", this._boundApplyFilters);
		}

		/** Attach event to tag button + update UI initially. */
		_setupTagButtons() {
			this.tagButtons.forEach((btn) => {
				if (btn) btn.addEventListener("click", this._boundOnTagClick);
			});
			this._updateTagButtonsUI();
		}

		/**
		 * Automatically load more page if after changing tag and no articles are displayed.
		 * Only run up to the last page (lastPage) to avoid infinite loop.
		 */
		async _ensureResultsForCurrentFilters() {
			if (!this.root || typeof this.root.loadNextPage !== "function") return;
			if (!this.grid) return;
			if (this._isAutoLoadingTag) return;

			const lastPage = Number(this.grid.dataset.lastPage || 1);
			let currentPage = 1;
			this.grid.querySelectorAll(SELECTORS.item).forEach((item) => {
				const pageAttr = Number(item.getAttribute("data-page") || 0);
				if (pageAttr > currentPage) currentPage = pageAttr;
			});

			if (!lastPage || currentPage >= lastPage) return;

			this._isAutoLoadingTag = true;
			try {
				await this.root.loadNextPage();
				this.applyFilters();
				this._updateLoadMoreVisibility();
			} finally {
				this._isAutoLoadingTag = false;
			}
		}

		/**
		 * Attach event to \"Load More Articles\" button if it exists and the component
		 * supports the loadNextPage method (from PaginatedList).
		 */
		_setupLoadMoreButton() {
			if (!this.loadMoreButton) return;
			if (typeof this.root.loadNextPage !== "function") {
				this.loadMoreButton.style.display = "none";
				return;
			}
			this._updateLoadMoreVisibility();
			this.loadMoreButton.addEventListener("click", this._boundOnLoadMoreClick);
		}

		/**
		 * Hide/show the load-more button based on the current page and total number of pages.
		 */
		_updateLoadMoreVisibility() {
			if (!this.grid || !this.loadMoreButton) return;
			const lastPage = Number(this.grid.dataset.lastPage || 1);
			let currentPage = 1;
			this.grid.querySelectorAll(SELECTORS.item).forEach((item) => {
				const pageAttr = Number(item.getAttribute("data-page") || 0);
				if (pageAttr > currentPage) currentPage = pageAttr;
			});
			if (!lastPage || currentPage >= lastPage) {
				this.loadMoreButton.style.display = "none";
			} else {
				this.loadMoreButton.style.display = "";
			}
		}

		/**
		 * Handler click on load-more button: call root.loadNextPage(),
		 * after that, apply the filter again and update the button state.
		 */
		async _onLoadMoreClick(e) {
			e.preventDefault();
			if (!this.root || typeof this.root.loadNextPage !== "function") return;
			if (!this.loadMoreButton) return;

			this.loadMoreButton.disabled = true;
			this.loadMoreButton.classList.add("opacity-70");

			try {
				await this.root.loadNextPage();
				this.applyFilters();
				this._updateLoadMoreVisibility();
			} finally {
				this.loadMoreButton.disabled = false;
				this.loadMoreButton.classList.remove("opacity-70");
			}
		}
	}

	/** Find all blog sections and create instances. */
	function initAll() {
		const sections = document.querySelectorAll("blog-posts-list[section-id]");
		sections.forEach((section) => {
			if (!section) return;
			const hasGrid = section.querySelector(SELECTORS.grid);
			const hasSearch = section.querySelector(SELECTORS.searchInput);
			if (hasGrid && hasSearch) {
				new BlogSearchFilter(section);
			}
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAll);
	} else {
		initAll();
	}
})();
